/*
These links were helpful
https://docs.mapbox.com/help/tutorials/use-mapbox-gl-js-with-react/
https://github.com/mapbox/mapbox-react-examples
*/
import styled from 'styled-components';
import MDBox from "components/MDBox";

import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useRef, useEffect, useState } from 'react';
import { downloadData } from 'aws-amplify/storage';
import mapboxgl from 'mapbox-gl';

import '@fontsource/roboto'; // Defaults to normal weight
import '@fontsource/roboto/700.css'; // Optional: Bold weight

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MapContainer = styled(MDBox)`
  position: relative;

  .mapboxgl-map {
    border-radius: 12px;
  }

  .map-legend {
    position: absolute;
    bottom: 20px; 
    right: 20px;
    background: rgba(255, 255, 255, 0.9); /* Semi-transparent white background */
    border-radius: 8px;
    padding: 10px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15); /* Subtle shadow */
    font-size: 12px;
    z-index: 999; /* Ensure it appears above the map */
    font-family: 'Roboto', sans-serif; /* Using Roboto */

    h4 {
      margin-bottom: 10px; /* Simplify margin */
      font-size: 14px; /* Adjust heading size for better readability */
      font-weight: 700; /* Bold for emphasis */
      text-align: center;
    }

    .gradient-bar {
      height: 15px;
      width: 200px;
      border-radius: 4px;
      background: linear-gradient(to right, lightblue, darkblue);
    }

    .legend-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 5px;

      span {
        font-size: 10px;
        color: #555; 
      }
    }
  }

  .mapboxgl-popup.custom-popup {
    .mapboxgl-popup-content {
      max-width: 700px 
      min-width: 250px
      padding: 12px 
      font-size: 14px 
      border-radius: 8px 
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) 
      font-family: 'Roboto', sans-serif 
      line-height: 1.15 
      color: #333 
    }

    .mapboxgl-popup-close-button {
      background-color: gray 
      color: white 
      border-radius: 20% 
      width: 18px 
      height: 18px 
      line-height: 18px 
    }
  }
`;
function StatemapComponent({downloadUrl}) {

  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [-98.5795, 39.8283],
      zoom: 3,
      attributionControl: false 
    });

    // zoom +/-
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    map.current.on('load', async () => {
      try{
          console.log("Fetching data from S3");
          const binaryData = await downloadData({path: downloadUrl}).result; //Ethan: changed the path to the correct one
          console.log("--------------------------------------------------------");
          const text = await binaryData.body.text();
          const geojsonData = JSON.parse(text);
          console.log("GeoJSON data parsed:", geojsonData);
          console.log("-------------------------------------");
          const data = geojsonData;
          //to make it similar to folium's, else can do this in other ways too
          const values = data.features.map(f => f.properties[["Solar Sentiment Score"]]);
          const minValue = Math.min(...values);
          const maxValue = Math.max(...values);
          const colorScale = [
            [minValue, 'lightblue'],
            [maxValue, 'darkblue']
          ];
          map.current.addSource('fips-data', {
            type: 'geojson',
            data: data
          });
          
          map.current.addLayer({
            id: 'fips-layer',
            type: 'fill',
            source: 'fips-data',
            paint: {
              'fill-color': [
                'interpolate',
                ['linear'],
                ['get', 'Solar Sentiment Score'],
                ...colorScale.flat()
              ],
              'fill-opacity': 0.7
            }
          });

         
          map.current.addLayer({
            id: 'fips-outline',
            type: 'line',
            source: 'fips-data',
            paint: {
              'line-color': '#000',
              'line-width': 1
            }
          });

          map.current.on('click', (e) => {
            const fipsFeatures = map.current.queryRenderedFeatures(e.point, {
              layers: ['fips-layer']  // Look for features on the FIPS layer
            });
        
            if (fipsFeatures.length > 0) {
              new mapboxgl.Popup({ className: 'custom-popup'}) // add custom class for styling
              .setLngLat(e.lngLat)
              .setHTML(`<span style="font-size: 1.5em"><strong>${fipsFeatures[0].properties.State}</strong> (${fipsFeatures[0].properties.Year})</span>
                <br><br><span style="font-size: 1.3em; color: 'darkblue'; fontWeight: " Semi Bold"">Ranked </span>
                <span style="font-size: 1.7em; color: 'darkblue'; fontWeight: " Semi Bold"">${fipsFeatures[0].properties["Ranking"].toFixed(0)}</span>
                <span style="font-size: 1.3em;"> in the nation for solar sentiment positivity</span>
                <br><br>Solar sentiment score of <strong>${fipsFeatures[0].properties["Solar Sentiment Score"].toFixed(2)}</strong> based on ${fipsFeatures[0].properties["No. of Posts"].toLocaleString()} posts from ${fipsFeatures[0].properties["No. of Unique Individuals"].toLocaleString()} unique X (Twitter) users`
              )
              .addTo(map.current);
            }
          });

          // Change cursor to pointer when hovering over a FIPS area
          map.current.on('mouseenter', 'fips-layer', () => {
            map.current.getCanvas().style.cursor = 'pointer';
          });

          // Change cursor back when leaving a FIPS area
          map.current.on('mouseleave', 'fips-layer', () => {
            map.current.getCanvas().style.cursor = '';
          });

          console.log("Map layers added successfully");
          
        }
        catch(error){
          console.error('Error loading GeoJSON:', error);
          setMapError('Error loading map data: ' + error.message);
        }
    });
  }, []);

  if (mapError) {
    // return <div>Error: {mapError}</div>;
    return <MDBox>Error: {mapError}</MDBox>;
  }

  return (
    <MapContainer ref={mapContainer} height="600px">
      <div className="map-legend">
        <h4>Solar Sentiment Score</h4>
        <div 
          className="gradient-bar" 
          style={{ background: 'linear-gradient(to right, lightblue, darkblue)' }} 
        ></div>
        <div className="legend-labels">
          <span>Negative (-10)</span>
          <span>Positive (+10)</span>
        </div>
      </div>
    </MapContainer>
  );
}

export default StatemapComponent;